import { z } from 'zod';
import { ClientCommandIdSchema } from './client-command-id';
import {
  SpaceIdSchema,
  SheetIdSchema,
  WorkbookIdSchema,
} from './entity-ids';
import {
  SheetSnapshotSchema,
  WorkbookCellPatchSchema,
  WorkbookCellSchema,
  WorkbookColumnSchema,
  WorkbookFreezeSchema,
  WorkbookMergeSchema,
  WorkbookRowSchema,
  WorkbookSheetDataSchema,
} from './workbook-types';

export const WorkbookOperationSourceSchema = z.enum(['manual', 'ai', 'system']);

export const WorkbookHistoryActionSchema = z.enum(['apply', 'undo', 'redo']);
export type WorkbookHistoryAction = z.infer<typeof WorkbookHistoryActionSchema>;

export const WorkbookCommandKindSchema = z.enum([
  'workbook.patch',
  'sheet.create',
  'sheet.delete',
  'sheet.patch',
  'sheet.reorder',
  'sheet.replaceData',
  'range.setValues',
  'range.setStyle',
  'rows.insert',
  'rows.delete',
  'cols.insert',
  'cols.delete',
  'rows.setSize',
  'cols.setSize',
  'range.setCells',
  'rows.setProps',
  'cols.setProps',
  'rows.move',
  'cols.move',
  'merges.add',
  'merges.remove',
]);

/** A cell input: '='-prefixed strings are formulas; null clears the cell. */
export const CellInputSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);
export type CellInput = z.infer<typeof CellInputSchema>;

export const SheetRangeSchema = z.object({
  startRow: z.number().int().nonnegative(),
  endRow: z.number().int().nonnegative(),
  startColumn: z.number().int().nonnegative(),
  endColumn: z.number().int().nonnegative(),
});
export type SheetRange = z.infer<typeof SheetRangeSchema>;

const IndexKeySchema = z.string().regex(/^\d+$/);

/**
 * A formula written back verbatim after a command applies. Inverse payloads
 * carry these so undoing a structural edit restores formulas it rewrote
 * (deleted references become #REF! and cannot be shifted back).
 */
export const FormulaCellSchema = z.object({
  sheetId: SheetIdSchema,
  row: z.number().int().nonnegative(),
  column: z.number().int().nonnegative(),
  f: z.string(),
});
export type FormulaCell = z.infer<typeof FormulaCellSchema>;

export const WorkbookPatchPayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  name: z.string().min(1).optional(),
});

export const SheetCreatePayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  insertAt: z.number().int().nonnegative().optional(),
  name: z.string().min(1).optional(),
  data: WorkbookSheetDataSchema.optional(),
  sheet: SheetSnapshotSchema.optional(),
  restoreFormulas: z.array(FormulaCellSchema).optional(),
});

export const SheetDeletePayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  deletedSheet: SheetSnapshotSchema.optional(),
});

/** Tab settings; a present field sets it, null clears it. */
export const SheetPatchPayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  name: z.string().min(1).optional(),
  tabColor: z.string().nullable().optional(),
  hidden: z.number().int().min(0).max(1).nullable().optional(),
  showGridlines: z.number().int().min(0).max(1).nullable().optional(),
  freeze: WorkbookFreezeSchema.nullable().optional(),
  rowCount: z.number().int().positive().optional(),
  columnCount: z.number().int().positive().optional(),
  restoreFormulas: z.array(FormulaCellSchema).optional(),
});

export const SheetReorderPayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  orderedSheetIds: z.array(SheetIdSchema).min(1),
});

export const SheetReplaceDataPayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  data: WorkbookSheetDataSchema,
});

export const RangeSetValuesPayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  startRow: z.number().int().nonnegative(),
  startColumn: z.number().int().nonnegative(),
  values: z.array(z.array(CellInputSchema)).min(1),
  /**
   * Exact prior cells (v/f/t; styles untouched), used by undo inverses.
   * When present it wins over `values` — a plain input matrix cannot
   * round-trip a formula cell's cached value or a literal '='-string.
   */
  priorCells: z.array(z.array(WorkbookCellSchema.nullable())).optional(),
});

/**
 * Style values are opaque IStyleData fragments; `null` clears the key.
 * Number formats ride here too (Univer's separate numfmt command family is
 * folded into `stylePatch.numberFormat` by the client mapper and by
 * format_range).
 */
export const StylePatchSchema = z.record(z.string(), z.unknown());

/**
 * Inverse form: prior per-cell style objects keyed by "row,col" offset within
 * the range (heterogeneous ranges can't invert through a single patch).
 */
export const RangeSetStylePayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  range: SheetRangeSchema,
  stylePatch: StylePatchSchema.optional(),
  priorCellStyles: z
    .record(z.string(), z.record(z.string(), z.unknown()).nullable())
    .optional(),
});

const DeletedRowSchema = z.object({
  cells: z.record(z.string(), WorkbookCellSchema),
  size: z.number().optional(),
  hidden: z.number().optional(),
  /** Rows only: measured content height and the "fit to content" switch. */
  autoSize: z.number().optional(),
  autoHeight: z.number().int().optional(),
});

/**
 * Content the grid puts into freshly inserted rows/columns (it copies the
 * neighbor's formatting and size), keyed by offset within the inserted block.
 */
const InsertedRowsSchema = z.object({
  rowData: z.record(IndexKeySchema, WorkbookRowSchema).optional(),
  cells: z
    .record(IndexKeySchema, z.record(IndexKeySchema, WorkbookCellSchema))
    .optional(),
});

const InsertedColsSchema = z.object({
  columnData: z.record(IndexKeySchema, WorkbookColumnSchema).optional(),
  /** Keyed by absolute row, then column offset within the inserted block. */
  cells: z
    .record(IndexKeySchema, z.record(IndexKeySchema, WorkbookCellSchema))
    .optional(),
});

export const RowsInsertPayloadSchema = z
  .object({
    workbookId: WorkbookIdSchema,
    sheetId: SheetIdSchema,
    index: z.number().int().nonnegative(),
    count: z.number().int().positive(),
  })
  .merge(InsertedRowsSchema);

export const RowsDeletePayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  index: z.number().int().nonnegative(),
  count: z.number().int().positive(),
  deletedRows: z.array(DeletedRowSchema).optional(),
  /** Pre-delete mergeData; restores verbatim since deletion clamps/drops merges irreversibly. */
  priorMerges: z.array(WorkbookMergeSchema).optional(),
  /** Pre-delete freeze; deletion clamps the frozen area irreversibly. */
  priorFreeze: WorkbookFreezeSchema.optional(),
  restoreFormulas: z.array(FormulaCellSchema).optional(),
});

export const ColsInsertPayloadSchema = z
  .object({
    workbookId: WorkbookIdSchema,
    sheetId: SheetIdSchema,
    index: z.number().int().nonnegative(),
    count: z.number().int().positive(),
  })
  .merge(InsertedColsSchema);

/**
 * Row heights / column widths in pixels, keyed by 0-based index; null clears
 * back to the sheet default. Self-inverting: the inverse is the same kind
 * with the captured prior sizes.
 */
export const AxisSizesSchema = z.record(
  z.string().regex(/^\d+$/),
  z.number().min(0).max(5000).nullable()
);

export const RowsSetSizePayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  sizes: AxisSizesSchema,
});

export const ColsSetSizePayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  sizes: AxisSizesSchema,
});

export const ColsDeletePayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  index: z.number().int().nonnegative(),
  count: z.number().int().positive(),
  deletedCols: z.array(DeletedRowSchema).optional(),
  /** Pre-delete mergeData; restores verbatim since deletion clamps/drops merges irreversibly. */
  priorMerges: z.array(WorkbookMergeSchema).optional(),
  priorFreeze: WorkbookFreezeSchema.optional(),
  restoreFormulas: z.array(FormulaCellSchema).optional(),
});

/**
 * Exact cell changes: sparse absolute row -> column -> patch. A null patch
 * removes the whole cell. Self-inverting: the inverse carries the prior
 * values of exactly the fields this one touched.
 */
export const RangeSetCellsPayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  cells: z.record(
    IndexKeySchema,
    z.record(IndexKeySchema, WorkbookCellPatchSchema.nullable())
  ),
});

const AxisSizeValueSchema = z.number().min(0).max(5000).nullable().optional();
const BooleanNumberPatchSchema = z
  .number()
  .int()
  .min(0)
  .max(1)
  .nullable()
  .optional();

/** Field-level row changes keyed by row index; null clears a field. */
export const RowsSetPropsPayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  props: z.record(
    IndexKeySchema,
    z.object({
      h: AxisSizeValueSchema,
      ah: AxisSizeValueSchema,
      ia: BooleanNumberPatchSchema,
      hd: BooleanNumberPatchSchema,
    })
  ),
});

export const ColsSetPropsPayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  props: z.record(
    IndexKeySchema,
    z.object({
      w: AxisSizeValueSchema,
      hd: BooleanNumberPatchSchema,
    })
  ),
});

/**
 * Move `count` rows/columns starting at `from` so they sit before index `to`
 * (an index in the pre-move grid, like Univer's move mutations: moving down
 * means to > from + count - 1 and the block lands at to - count).
 */
export const AxisMovePayloadSchema = z
  .object({
    workbookId: WorkbookIdSchema,
    sheetId: SheetIdSchema,
    from: z.number().int().nonnegative(),
    count: z.number().int().positive(),
    to: z.number().int().nonnegative(),
    restoreFormulas: z.array(FormulaCellSchema).optional(),
    priorFreeze: WorkbookFreezeSchema.optional(),
  })
  .refine((move) => move.to <= move.from || move.to >= move.from + move.count, {
    message: 'to must be outside the moved block',
  });

export const MergesPayloadSchema = z.object({
  workbookId: WorkbookIdSchema,
  sheetId: SheetIdSchema,
  ranges: z.array(WorkbookMergeSchema),
});

export const WorkbookCommandSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('workbook.patch'),
    payload: WorkbookPatchPayloadSchema,
  }),
  z.object({
    kind: z.literal('sheet.create'),
    payload: SheetCreatePayloadSchema,
  }),
  z.object({
    kind: z.literal('sheet.delete'),
    payload: SheetDeletePayloadSchema,
  }),
  z.object({
    kind: z.literal('sheet.patch'),
    payload: SheetPatchPayloadSchema,
  }),
  z.object({
    kind: z.literal('sheet.reorder'),
    payload: SheetReorderPayloadSchema,
  }),
  z.object({
    kind: z.literal('sheet.replaceData'),
    payload: SheetReplaceDataPayloadSchema,
  }),
  z.object({
    kind: z.literal('range.setValues'),
    payload: RangeSetValuesPayloadSchema,
  }),
  z.object({
    kind: z.literal('range.setStyle'),
    payload: RangeSetStylePayloadSchema,
  }),
  z.object({
    kind: z.literal('rows.insert'),
    payload: RowsInsertPayloadSchema,
  }),
  z.object({
    kind: z.literal('rows.delete'),
    payload: RowsDeletePayloadSchema,
  }),
  z.object({
    kind: z.literal('cols.insert'),
    payload: ColsInsertPayloadSchema,
  }),
  z.object({
    kind: z.literal('cols.delete'),
    payload: ColsDeletePayloadSchema,
  }),
  z.object({
    kind: z.literal('rows.setSize'),
    payload: RowsSetSizePayloadSchema,
  }),
  z.object({
    kind: z.literal('cols.setSize'),
    payload: ColsSetSizePayloadSchema,
  }),
  z.object({
    kind: z.literal('range.setCells'),
    payload: RangeSetCellsPayloadSchema,
  }),
  z.object({
    kind: z.literal('rows.setProps'),
    payload: RowsSetPropsPayloadSchema,
  }),
  z.object({
    kind: z.literal('cols.setProps'),
    payload: ColsSetPropsPayloadSchema,
  }),
  z.object({
    kind: z.literal('rows.move'),
    payload: AxisMovePayloadSchema,
  }),
  z.object({
    kind: z.literal('cols.move'),
    payload: AxisMovePayloadSchema,
  }),
  z.object({
    kind: z.literal('merges.add'),
    payload: MergesPayloadSchema,
  }),
  z.object({
    kind: z.literal('merges.remove'),
    payload: MergesPayloadSchema,
  }),
]);
export type WorkbookCommand = z.infer<typeof WorkbookCommandSchema>;

export const WorkbookCommandEnvelopeSchema = z.object({
  clientCommandId: ClientCommandIdSchema,
  clientId: z.string().min(1),
  actorSessionId: z.string().min(1).optional(),
  actorRunId: z.string().min(1).optional(),
  source: WorkbookOperationSourceSchema.default('manual'),
  groupId: z.string().min(1).optional(),
  baseSequence: z.number().int().nonnegative(),
  command: WorkbookCommandSchema,
});
export type WorkbookCommandEnvelope = z.infer<
  typeof WorkbookCommandEnvelopeSchema
>;

export const WorkbookOperationSchema = z.object({
  id: z.string(),
  workbookId: WorkbookIdSchema,
  sequence: z.number().int().nonnegative(),
  actorUserId: z.string().nullable().optional(),
  actorRunId: z.string().nullable().optional(),
  actorSessionId: z.string().nullable().optional(),
  clientId: z.string().nullable().optional(),
  clientCommandId: z.string().nullable().optional(),
  source: WorkbookOperationSourceSchema,
  groupId: z.string().nullable().optional(),
  kind: WorkbookCommandKindSchema,
  historyAction: WorkbookHistoryActionSchema,
  targetOperationId: z.string().nullable().optional(),
  baseSequence: z.number().int().nonnegative(),
  payload: z.unknown(),
  inversePayload: z.unknown(),
  affectedSheetIds: z.array(SheetIdSchema),
  createdAt: z.string(),
});
export type WorkbookOperation = z.infer<typeof WorkbookOperationSchema>;

// Wire shape returned by /commands, /history/undo, and /history/redo.
export const WorkbookCommandResultSheetSchema = z.object({
  id: SheetIdSchema,
  workbookId: WorkbookIdSchema,
  order: z.number().int().nonnegative(),
  name: z.string(),
  data: WorkbookSheetDataSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WorkbookCommandResultSheet = z.infer<
  typeof WorkbookCommandResultSheetSchema
>;

export const WorkbookCommandResultWorkbookSchema = z.object({
  id: WorkbookIdSchema,
  name: z.string(),
  organizationId: z.string(),
  spaceId: SpaceIdSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  sheetCount: z.number().int().nonnegative(),
  headSequence: z.number().int().nonnegative(),
});
export type WorkbookCommandResultWorkbook = z.infer<
  typeof WorkbookCommandResultWorkbookSchema
>;

export const WorkbookCommandResultSchema = z.object({
  headSequence: z.number().int().nonnegative(),
  /** The last (primary) operation; `operations` lists every member of a
   * grouped undo/redo in execution order. */
  operation: WorkbookOperationSchema,
  operations: z.array(WorkbookOperationSchema),
  changedSheets: z.array(WorkbookCommandResultSheetSchema),
  deletedSheetIds: z.array(SheetIdSchema),
  workbook: WorkbookCommandResultWorkbookSchema.optional(),
});
export type WorkbookCommandResult = z.infer<typeof WorkbookCommandResultSchema>;

export const WorkbookHistoryCursorSchema = z.object({
  undoAvailable: z.boolean(),
  redoAvailable: z.boolean(),
});
export type WorkbookHistoryCursor = z.infer<typeof WorkbookHistoryCursorSchema>;

export const WorkbookUndoRedoRequestSchema = z.object({
  clientCommandId: ClientCommandIdSchema,
  clientId: z.string().min(1),
  actorSessionId: z.string().min(1).optional(),
  baseSequence: z.number().int().nonnegative(),
  operationId: z.string().min(1).optional(),
  /** Steps this tab was refused before; Ctrl+Z moves past them. */
  skippedOperationIds: z.array(z.string().min(1)).max(500).optional(),
});
export type WorkbookUndoRedoRequest = z.infer<
  typeof WorkbookUndoRedoRequestSchema
>;
